import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { Campaign } from '@features/campaigns';
import {
  CalculationReturn,
  useReturnOnAdSpend,
} from '@features/campaigns/hooks/useCalculatedFields';
import { useAdSpend } from '@features/campaigns/subdomains/visitation/hooks/useAdSpend';
import { useGetCostPerVisitCalculationQuery } from '@features/reports';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

export type UseCostPerVisitOptions = {
  report:
    | (Pick<Lci3Report, 'reportDate'> & {
        visitMetrics: Pick<
          Lci3Report['visitMetrics'],
          'projectedVisits' | 'incrementalVisits'
        >;
      })
    | null;
  campaign: Pick<Campaign, 'startDate' | 'endDate' | 'id'>;
  visitsField: 'projectedVisits' | 'incrementalVisits';
  adSpend?: number;
};

export const useCostPerVisit = (
  options: UseCostPerVisitOptions
): CalculationReturn => {
  const { report, campaign, visitsField, adSpend: adSpendOverride } = options;
  const { adSpend: fetchedAdSpend, isFetching: isFetchingAdSpend } = useAdSpend(
    {
      campaignId: campaign.id,
    }
  );

  const { data, isFetching: isFetchingCalculation } =
    useGetCostPerVisitCalculationQuery(
      report
        ? {
            start_date: campaign.startDate,
            end_date: campaign.endDate,
            report_date: report.reportDate,
            visits: report.visitMetrics[visitsField] ?? 0,
            ad_spend: adSpendOverride ?? fetchedAdSpend ?? 0,
          }
        : skipToken
    );

  const value = data?.data?.cost_per_visit ?? null;
  const isFetching = isFetchingCalculation || isFetchingAdSpend;

  return useMemo(
    () => ({
      value,
      isFetching,
    }),
    [value, isFetching]
  );
};

type UseCalculatedFieldsOptions = {
  report?: Pick<Lci3Report, 'reportDate' | 'visitMetrics'>;
  salesReport?: Pick<Lci3Report, 'reportDate' | 'salesMetrics'>;
  campaign: Pick<Campaign, 'startDate' | 'endDate' | 'id'>;
};

export type UseCalculatedFieldsReturn = {
  isFetching: boolean;
  data: {
    adSpend: number | null;
    costPerVisit: number | null;
    costPerIncrementalVisit: number | null;
    returnOnAdSpend: number | null;
    incrementalReturnOnAdSpend: number | null;
  };
};

export const useCalculatedFields = (
  options: UseCalculatedFieldsOptions
): UseCalculatedFieldsReturn => {
  const { report, salesReport, campaign } = options;
  const { adSpend, isFetching: isFetchingAdSpend } = useAdSpend({
    campaignId: campaign.id,
  });

  const { value: costPerVisit, isFetching: isFetchingCpv } = useCostPerVisit({
    report: report ?? null,
    campaign,
    visitsField: 'projectedVisits',
  });

  const { value: costPerIncrementalVisit, isFetching: isFetchingCpiv } =
    useCostPerVisit({
      report: report ?? null,
      campaign,
      visitsField: 'incrementalVisits',
    });

  const { value: returnOnAdSpend, isFetching: isFetchingRoas } =
    useReturnOnAdSpend({
      reportDate: salesReport?.reportDate ?? null,
      campaign,
      sales: salesReport?.salesMetrics?.totalProjectedSales.total ?? null,
      adSpend: adSpend ?? null,
    });

  const { value: incrementalReturnOnAdSpend, isFetching: isFetchingIroas } =
    useReturnOnAdSpend({
      reportDate: salesReport?.reportDate ?? null,
      campaign,
      sales: salesReport?.salesMetrics?.incrementalSales.total ?? null,
      adSpend: adSpend ?? null,
    });

  return useMemo(
    () => ({
      isFetching:
        isFetchingCpv ||
        isFetchingCpiv ||
        isFetchingRoas ||
        isFetchingIroas ||
        isFetchingAdSpend,
      data: {
        adSpend,
        costPerVisit,
        costPerIncrementalVisit,
        returnOnAdSpend,
        incrementalReturnOnAdSpend,
      },
    }),
    [
      costPerVisit,
      costPerIncrementalVisit,
      returnOnAdSpend,
      incrementalReturnOnAdSpend,
      isFetchingCpv,
      isFetchingCpiv,
      isFetchingRoas,
      isFetchingIroas,
      isFetchingAdSpend,
      adSpend,
    ]
  );
};
